import React, { useEffect, useMemo, useState } from 'react';

import { useWindowSize } from '../../hooks';
import { getMember } from '../../services/core/getMember';
import { getLaunchdarkly } from '../../services/getLaunchdarkly';
import type { ICrossSell, Member } from './types';

import Checkbox from '../Checkbox';
import Tooltip from '../Tooltip';

const MOBILE_WIDTH = 1120;

export interface CrossSellProps {
  isChecked: boolean;
  onChecked: (value: boolean) => void;
}

const CrossSell = ({ isChecked, onChecked, ...props }: CrossSellProps) => {
  const [member, setMember] = useState<Member>({
    isClubMember: false,
    category: '',
  });
  const [launchdarklyData, setLaunchdarlyData] = useState<ICrossSell | null>(
    null
  );

  const { width } = useWindowSize();
  const isMobile = width <= MOBILE_WIDTH;

  const isSafariUserAGent = /Apple/.test(navigator.vendor);

  const isEnableCheckBox = useMemo(() => {
    if (isSafariUserAGent && isMobile) {
      return false;
    }

    // Disable by general property and if the member is club and isn't enabled club
    if (
      !launchdarklyData ||
      !launchdarklyData.enableAll ||
      !launchdarklyData.products.flights ||
      (member.isClubMember && !launchdarklyData.enableClub)
    ) {
      return false;
    }

    // Disable by device
    const { desktop, mobile } = launchdarklyData.device;
    if ((!isMobile && !desktop) || (isMobile && !mobile)) {
      return false;
    }

    // Disable by category tier
    const category = member.category.toLowerCase();
    const crossSellTiers = launchdarklyData.tier;
    const tiersEnabled = Object.keys(crossSellTiers).filter(
      (tier) => crossSellTiers[tier as keyof ICrossSell['tier']]
    );

    if (member.category && !tiersEnabled.includes(category)) {
      return false;
    }

    onChecked(true);

    return true;
  }, [launchdarklyData, member, isMobile, onChecked, isSafariUserAGent]);

  const onGetMember = async () => {
    const response = await getMember();

    if (response?.data) {
      setMember(response.data as Member);
    }
  };

  const onGetLaunchdarkly = async () => {
    const response = await getLaunchdarkly();

    if (response) {
      setLaunchdarlyData(response);

      await onGetMember();
    }
  };

  useEffect(() => {
    onGetLaunchdarkly();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isEnableCheckBox) {
    return null;
  }

  return (
    <div className="smls-cross-sell" {...props}>
      <Checkbox
        id="cross-sell-checkbox"
        name="cross-sell-checkbox"
        isChecked={isChecked}
        onCheck={() => onChecked(!isChecked)}
        className="smls-cross-sell-checkbox"
      />

      <span className="smls-cross-sell-description">
        Incluir busca por hotéis
        {isMobile ? (
          <span>Marque para buscar hotéis em nova aba</span>
        ) : (
          <Tooltip
            position="top-right"
            text="Abre nova aba de busca"
            containerClassName="smls-cross-sell-description-tooltip"
            tooltipClassName="smls-cross-sell-description-tooltip-content"
          >
            <i className="smls-cross-sell-description-tooltip-icon material-icons-outlined">
              info
            </i>
          </Tooltip>
        )}
      </span>
    </div>
  );
};

export default CrossSell;
